import { useState } from "react";
import { createConstellation } from "./supabaseClient";
import { useTheme, PageWrap, ThemeToggle, Nav, SERIF, SANS, PLAY, btnStyle, inputStyle as baseInputStyle, labelStyle } from "./theme";

export default function NewConstellation() {
  var { mode, t, toggle } = useTheme();
  var [form, setForm] = useState({ recipient_name: "", occasion: "", creator_name: "", creator_email: "", company: "" });
  var [anonymous, setAnonymous] = useState(false);
  var [result, setResult] = useState(null);
  var [loading, setLoading] = useState(false);
  var [copied, setCopied] = useState(false);

  var handleCreate = async function() {
    if (!form.recipient_name || !form.creator_name || !form.creator_email) return;
    setLoading(true);
    var data = await createConstellation({ ...form, vertical: "corporate", anonymous: anonymous });
    setResult(data);
    setLoading(false);
  };

  var shareUrl = result ? window.location.origin + "/contribute?c=" + result.slug : "";
  var viewUrl = result ? window.location.origin + "/constellation?c=" + result.slug : "";
  var iStyle = baseInputStyle(t);
  var ready = form.recipient_name && form.creator_name && form.creator_email;

  return (
    <PageWrap t={t}>
      <style>{`
        @keyframes ytFadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .yt-input:focus { border-color: ${t.accent} !important; }
        .yt-btn { transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94) !important; cursor: pointer !important; }
        .yt-btn:hover { transform: translateY(-2px) !important; letter-spacing: 3px !important; }
      `}</style>
      <ThemeToggle t={t} toggle={toggle} mode={mode} />
      <Nav t={t} />

      <div style={{ maxWidth: 520, margin: "0 auto", padding: "80px 24px 120px" }}>
        {!result ? (
          <div style={{ animation: "ytFadeIn 0.8s ease-out" }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 8, color: t.accentSoft, textTransform: "uppercase", marginBottom: 28 }}>Yours Truly</p>
              <h1 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(28px, 5vw, 42px)", fontStyle: "italic", color: t.text, margin: "0 0 16px", lineHeight: 1.2, letterSpacing: 1 }}>
                Create a Constellation
              </h1>
              <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 16, fontWeight: 300, color: t.textSoft, lineHeight: 1.9, maxWidth: 420, margin: "0 auto" }}>
                Set up a personalized constellation for someone special. Share the link — each person contributes a star.
              </p>
            </div>

            {/* Form */}
            <div style={{ display: "grid", gap: 24 }}>
              <div>
                <label style={labelStyle(t)}>Who is this for?</label>
                <input className="yt-input" placeholder="Recipient's full name" value={form.recipient_name}
                  onChange={function(e) { setForm({ ...form, recipient_name: e.target.value }); }}
                  style={iStyle} />
              </div>
              <div>
                <label style={labelStyle(t)}>What's the occasion?</label>
                <input className="yt-input" placeholder="e.g. retirement, farewell, milestone" value={form.occasion}
                  onChange={function(e) { setForm({ ...form, occasion: e.target.value }); }}
                  style={iStyle} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle(t)}>Your name</label>
                  <input className="yt-input" placeholder="Your name" value={form.creator_name}
                    onChange={function(e) { setForm({ ...form, creator_name: e.target.value }); }}
                    style={iStyle} />
                </div>
                <div>
                  <label style={labelStyle(t)}>Your email</label>
                  <input className="yt-input" placeholder="you@company.com" type="email" value={form.creator_email}
                    onChange={function(e) { setForm({ ...form, creator_email: e.target.value }); }}
                    style={iStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle(t)}>Company (optional)</label>
                <input className="yt-input" placeholder="Company name" value={form.company}
                  onChange={function(e) { setForm({ ...form, company: e.target.value }); }}
                  style={iStyle} />
              </div>

              {/* Anonymous toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0", borderTop: "1px solid " + t.border }}>
                <button onClick={function() { setAnonymous(!anonymous); }} style={{
                  width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                  background: anonymous ? t.accent : t.inputBg,
                  position: "relative", transition: "background 0.3s", flexShrink: 0,
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 9, background: anonymous ? t.btnText : t.textMuted,
                    position: "absolute", top: 3, left: anonymous ? 23 : 3,
                    transition: "left 0.3s, background 0.3s",
                  }} />
                </button>
                <div>
                  <p style={{ fontFamily: SANS, fontSize: 13, fontWeight: 400, color: t.text, margin: "0 0 2px" }}>Anonymous contributions</p>
                  <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 300, color: t.textMuted, margin: 0 }}>
                    {anonymous ? "Contributor names will be hidden on the crystal" : "Contributors' names will appear alongside their words"}
                  </p>
                </div>
              </div>

              {/* Submit */}
              <button className="yt-btn" onClick={handleCreate} disabled={loading || !ready} style={{
                ...btnStyle(t),
                width: "100%", padding: "18px 0", marginTop: 8, textAlign: "center",
                opacity: ready ? 1 : 0.35, cursor: ready ? "pointer" : "default",
              }}>
                {loading ? "Creating..." : "Create Constellation"}
              </button>
            </div>
          </div>
        ) : (
          /* ═══ SUCCESS STATE ═══ */
          <div style={{ textAlign: "center", animation: "ytFadeIn 0.8s ease-out" }}>
            <div style={{ fontSize: 9, letterSpacing: 8, textTransform: "uppercase", color: t.accentSoft, marginBottom: 28, fontFamily: SANS }}>Constellation Created</div>

            <p style={{ fontFamily: SANS, fontSize: 48, color: t.accent, margin: "0 0 28px", lineHeight: 1 }}>✦</p>

            <h2 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(24px, 4vw, 36px)", fontStyle: "italic", color: t.text, margin: "0 0 16px", lineHeight: 1.3 }}>
              {form.recipient_name}'s constellation is live
            </h2>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15, fontWeight: 300, color: t.textSoft, lineHeight: 1.9, marginBottom: 48, maxWidth: 400, margin: "0 auto 48px" }}>
              Share this link. Each person takes two minutes to share what {form.recipient_name} means to them. Each response becomes a star.
            </p>

            {/* Share link */}
            <div style={{
              padding: "16px 20px", marginBottom: 20,
              background: t.cardBg, border: "1px solid " + t.accentMuted,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14,
            }}>
              <span style={{ fontSize: 13, fontFamily: SANS, fontWeight: 300, color: copied ? t.text : t.accent, wordBreak: "break-all", transition: "color 0.3s" }}>{shareUrl}</span>
              <button onClick={function() { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }} style={{
                ...btnStyle(t, "secondary"), padding: "8px 20px", fontSize: 9, flexShrink: 0,
                background: copied ? t.accent : t.btnSecBg,
                color: copied ? t.btnText : t.btnSecText,
                borderColor: copied ? t.accent : t.btnSecBorder,
                transition: "all 0.3s",
              }}>
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>

            <p style={{ fontSize: 12, fontFamily: SANS, fontWeight: 300, color: t.textMuted, marginBottom: 40 }}>
              Send this to anyone who knows {form.recipient_name}. More contributors, more stars, more beautiful.
            </p>

            {/* View link */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
              <a href={shareUrl} className="yt-btn" style={{ ...btnStyle(t), padding: "16px 36px", textDecoration: "none" }}>
                Add your own star
              </a>
              <a href={viewUrl} className="yt-btn" style={{ ...btnStyle(t, "secondary"), padding: "16px 36px" }}>
                View constellation progress
              </a>
            </div>
          </div>
        )}
      </div>
    </PageWrap>
  );
}
